import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Heart-shaped favorite toggle for events.
 * Two variants:
 *  - variant="overlay": absolute-positioned pill (used on top of event cards)
 *  - variant="inline":  button-sized standalone (used on event detail page)
 */
const FavoriteButton = ({ eventId, variant = 'overlay', size = 20 }) => {
  const { user, isFavorite, toggleFavorite } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(false);

  const active = user ? isFavorite(eventId) : false;

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      toast.info('Iniciá sesión para guardar tus favoritos');
      return;
    }
    if (pending) return;
    setPending(true);
    try {
      const nowFav = await toggleFavorite(eventId);
      toast.success(nowFav ? 'Guardado en tus favoritos' : 'Quitado de tus favoritos');
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Tu sesión expiró. Iniciá sesión de nuevo.');
        navigate('/login');
      } else {
        toast.error('No pudimos actualizar el favorito. Intentá de nuevo.');
      }
    } finally {
      setPending(false);
    }
  };

  const baseCls =
    variant === 'overlay'
      ? 'absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md shadow-md transition-transform active:scale-95'
      : 'inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors';

  const stateCls = active
    ? variant === 'overlay'
      ? 'bg-primary text-white'
      : 'bg-primary text-white border-primary'
    : variant === 'overlay'
    ? 'bg-white/90 text-[#0A0A0A] hover:bg-white'
    : 'bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={active}
      disabled={pending}
      className={`${baseCls} ${stateCls}`}
      data-testid={`favorite-button-${eventId}`}
      aria-label={active ? 'Quitar de favoritos' : 'Guardar en favoritos'}
      title={active ? 'Quitar de favoritos' : 'Guardar en favoritos'}
    >
      <Heart className={active ? 'fill-current' : ''} size={size} />
      {variant === 'inline' && (
        <span className="text-sm font-medium">{active ? 'Guardado' : 'Guardar'}</span>
      )}
    </button>
  );
};

export default FavoriteButton;
